import React, { useState } from 'react';
import { Button, Tooltip, CircularProgress } from '@mui/material';
import * as FileSaver from 'file-saver';
import XLSX from 'sheetjs-style';
import { format, parse } from 'date-fns';
import { retrieveShippingInstructionClosingDateApi } from '../api/AuditLogApiService';

const ShippingInstructionClosingExport = ({ fileName = 'ATA_Dates', buttonLabel = 'ATA Dates', buttonProps = {} }) => {
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    try {
      setLoading(true);
      const res = await retrieveShippingInstructionClosingDateApi();
      const data = Array.isArray(res.data) ? res.data : [];

      const normalizeEscapes = (s) => s.replace(/\\u003C/g, '<').replace(/\\u003E/g, '>');

      const simplifyDateString = (v) => {
        if (!v) return '';
        const s = (v || '').toString().trim();
        if (!s || s.toLowerCase() === 'null') return '';
        const m = s.match(/(\d{1,2}-[A-Za-z]{3}-\d{4})/);
        try {
          if (m) {
            const d = parse(m[1], 'dd-MMM-yyyy', new Date());
            return format(d, 'dd/MM/yyyy');
          }
          const maybe = new Date(s);
          if (!Number.isNaN(maybe.getTime())) return format(maybe, 'dd/MM/yyyy');
        } catch (e) {
          // fallthrough
        }
        return s;
      };

      const parseChanges = (html) => {
        const out = {};
        if (!html) return out;
        let s = normalizeEscapes(html);
        s = s.replace(/<br\s*\/?\>/gi, '\n');
        const el = document.createElement('div');
        el.innerHTML = s;

        const bolds = el.querySelectorAll('b');
        bolds.forEach(b => {
          const field = (b.textContent || '').trim();
          let from = '';
          let to = '';
          // iterate siblings after this <b> until next <b> or end
          let node = b.nextSibling;
          const iValues = [];
          while (node) {
            if (node.nodeType === 1 && node.tagName && node.tagName.toLowerCase() === 'b') break;
            if (node.nodeType === 1 && node.tagName && node.tagName.toLowerCase() === 'i') {
              iValues.push((node.textContent || '').trim());
            }
            node = node.nextSibling;
          }
          from = simplifyDateString(iValues[0] || '');
          to = simplifyDateString(iValues[1] || '');
          out[`${field}_from`] = from;
          out[`${field}_to`] = to;
        });

        return out;
      };

      // first build parsed rows and collect all field keys
      const parsedRows = data.map(item => {
        const parsed = parseChanges(item.changes);
        return {
          id: item.id,
          entityType: item.entityType,
          entityId: item.entityId,
          username: item.username,
          modifiedAt: item.modifiedAt ? format(new Date(item.modifiedAt), 'dd/MM/yyyy HH:mm') : '' ,
          ...parsed
        };
      });

      const allKeys = new Set();
      parsedRows.forEach(r => Object.keys(r).forEach(k => allKeys.add(k)));
      // ensure consistent column order: base columns first
      const baseCols = ['id','entityType','entityId','username','modifiedAt'];
      const extraCols = Array.from(allKeys).filter(k => !baseCols.includes(k)).sort();
      const orderedRows = parsedRows.map(r => {
        const row = {};
        baseCols.forEach(c => row[c] = r[c] || '');
        extraCols.forEach(c => row[c] = r[c] || '');
        return row;
      });

      const excelData = orderedRows.sort((a,b) => Number(b.entityId || 0) - Number(a.entityId || 0));

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset-UTF-8';
      const dataBlob = new Blob([excelBuffer], { type: fileType });
      FileSaver.saveAs(dataBlob, fileName + '.xlsx');
    } catch (err) {
      console.error('Export failed', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip title="Download ATA entrance dates as Excel">
      <span>
        <Button
          variant="outlined"
          size="small"
          onClick={handleExport}
          disabled={loading}
          {...buttonProps}
        >
          {loading ? <CircularProgress size={18} /> : buttonLabel}
        </Button>
      </span>
    </Tooltip>
  );
};

export default ShippingInstructionClosingExport;
